import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { EventService } from './event.service';
import {
  AuthenticationService,
  MarketplacePermission,
} from '@libs/authentication/services';
import { SessionStartedEvent } from '../types';
import { UserDataService } from '@libs/services/user-data';
import { distinctUntilChanged, filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

const employerProductId = 'EMPLOYER_WEB';

@Injectable({
  providedIn: 'root',
})
export class UserLifecycleService {
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private auth = inject(AuthenticationService);
  private eventService = inject(EventService);
  private userDataService = inject(UserDataService);
  private authPermission = toSignal(this.auth.permission$);

  // lifecycles don't quite work as expected in angular for services, service has to be
  // included in a component and a function called inside of it
  init(): void {
    if (!this.isBrowser) {
      // don't need to listen or process any other events on the server
      return;
    }
    this.eventService.updateContext({
      AppInstanceId: this.userDataService.appInstanceId,
    });

    // set initial context of data about the user, appInstanceId and the sessionId
    this.auth.permission$
      .pipe(
        filter((p: MarketplacePermission) => !!p?.emailAddress),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.eventService.updateContext({});
      });

    this.sendSessionStartEvent();
  }

  private sendSessionStartEvent(): void {
    const sessionId = this.auth.getSessionId();
    const userAgent = window?.navigator?.userAgent;

    this.eventService.send(
      new SessionStartedEvent({
        ContextId: sessionId,
        UserAgent: userAgent ?? null,
        ProductId: employerProductId,
        DeviceTimezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone ?? null,
        ReferringUrl: document.referrer ?? null,
        RefCode: this.userDataService.refCode()?.refCode,
        RefSource: this.userDataService.refCode()?.refSource,
        MemberId: this.authPermission()?.memberId ?? null,
      }),
    );
  }
}
